import React, { useState, useEffect, useCallback } from 'react';
import { message, Select, Table, DatePicker, Checkbox, Button, Space, Modal, Form, Input, Upload, Spin, Alert, Typography, Tag, Tooltip, Card, Tabs, Divider, Empty } from 'antd';
import { 
  CloudUploadOutlined, 
  PlusOutlined, 
  DeleteOutlined, 
  InfoCircleOutlined, 
  FileOutlined,
  DatabaseOutlined,
  ReloadOutlined,
  CloudOutlined
} from '@ant-design/icons';
import axios from 'axios';
import config from '../config';

const { Text, Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const VectorStoreManagement = () => {
  const [form] = Form.useForm();
  const [vectorStores, setVectorStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingStores, setFetchingStores] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentStore, setCurrentStore] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('openai');
  const [contentType, setContentType] = useState('post');
  const [categories, setCategories] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [authors, setAuthors] = useState([]);
  const [authorOptions, setAuthorOptions] = useState([]);
  const [posts, setPosts] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingAuthors, setLoadingAuthors] = useState(false);
  // Added state for post types
  const [postTypes, setPostTypes] = useState([]);
  const [loadingPostTypes, setLoadingPostTypes] = useState(false);

  const [openaiStores, setOpenaiStores] = useState([]);
  const [fetchingOpenaiStores, setFetchingOpenaiStores] = useState(false);
  const [openaiFiles, setOpenaiFiles] = useState([]);
  const [fetchingOpenaiFiles, setFetchingOpenaiFiles] = useState(false);
  const [selectedOpenaiStore, setSelectedOpenaiStore] = useState(null);
  const [openaiFilesModalVisible, setOpenaiFilesModalVisible] = useState(false);
  const [openaiStoreError, setOpenaiStoreError] = useState(null);

  const DEBUG_MODE = false;

  // Console wrapper
  const debugLog = {
      log: (...args) => {
          if (DEBUG_MODE) console.log(...args);
      },
      error: (...args) => {
          if (DEBUG_MODE) console.error(...args);
      },
      warn: (...args) => {
          if (DEBUG_MODE) console.warn(...args);
      }
  };

  // Replace all console references
  const console = debugLog;

  // Fetch vector stores on component mount
  useEffect(() => {
    fetchVectorStores();
    // Also fetch post types on component mount
    fetchPostTypes();
  }, []);

  useEffect(() => {
    if (activeTab === 'openai-list') {
      fetchOpenaiVectorStores();
    }
  }, [activeTab]);

  // Add function to fetch post types
  const fetchPostTypes = async () => {
    try {
      setLoadingPostTypes(true);
      console.log('Fetching post types...');
      
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/post-types`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      console.log('Post types API response:', response.data);
      
      // Check if we need to transform the data from the API
      let postTypeData = response.data;
      if (response.data.post_types) {
        // If the API returns data in a nested format
        postTypeData = response.data.post_types;
        console.log('Using nested post_types field:', postTypeData);
      }
      
      // Ensure each post type has rest_base information
      const enhancedPostTypes = postTypeData.map(type => {
        if (!type.rest_base) {
          // For standard post types, we can make a reasonable guess
          if (type.name === 'post') {
            return {...type, rest_base: 'posts'};
          } else if (type.name === 'page') {
            return {...type, rest_base: 'pages'};
          } else if (type.name === 'product') {
            return {...type, rest_base: 'products'};
          } else {
            // For custom post types, try pluralizing or use the type name
            return {...type, rest_base: `${type.name}s`};
          }
        }
        return type;
      });
      
      console.log('Enhanced post types with rest_base:', enhancedPostTypes);
      
      setPostTypes(enhancedPostTypes);
      
      // If post types were fetched successfully and there are post types,
      // set the default contentType to the first post type
      if (enhancedPostTypes && enhancedPostTypes.length > 0) {
        console.log('Setting default content type to:', enhancedPostTypes[0].name);
        setContentType(enhancedPostTypes[0].name);
      }
    } catch (error) {
      console.error('Error fetching post types:', error);
      console.error('Error details:', error.response || error.message);
      message.error('Failed to fetch post types');
    } finally {
      setLoadingPostTypes(false);
    }
  };

  const fetchVectorStores = async () => {
    try {
      setFetchingStores(true);
      setError(null);
      
      // Replace with your actual API endpoint for fetching vector stores
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/vector-stores`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      setVectorStores(response.data);
    } catch (error) {
      console.error('Error fetching vector stores:', error);
      setError('Failed to fetch vector stores. Please ensure you have set up your OpenAI API key in the settings.');
    } finally {
      setFetchingStores(false);
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(`${config.apiSettings.root}wp/v2/categories`, {
        params: { per_page: 100 }
      });
      
      const options = response.data.map(cat => ({
        label: cat.name,
        value: cat.id.toString()
      }));
      setCategoryOptions(options);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  }, []);
  
  const fetchAuthors = useCallback(async () => {
    try {
        setLoadingAuthors(true);
        const response = await axios.get(
            `${config.apiSettings.root}wp-ai-workflows/v1/authors`,
            {
                headers: {
                    'X-WP-Nonce': config.apiSettings.nonce
                }
            }
        );
        
        setAuthorOptions(response.data); // Response is already formatted for the Select component
    } catch (error) {
        console.error('Error fetching authors:', error);
        message.error('Failed to load authors');
    } finally {
        setLoadingAuthors(false);
    }
  }, []);
  
  // Function to fetch filtered posts
  const fetchFilteredPosts = async () => {
    setLoadingContent(true);
    try {
        const params = {
            post_type: contentType,
            per_page: 100,
            status: 'publish'
        };
        
        if (categories.length > 0) {
            params.categories = categories.join(',');
        }
        
        if (dateRange[0] && dateRange[1]) {
            params.after = dateRange[0].format('YYYY-MM-DD');
            params.before = dateRange[1].format('YYYY-MM-DD');
        }
        
        if (authors.length > 0) {
            params.authors = authors.join(',');
        }
        
        console.log('Fetching filtered posts with content type:', contentType);
        console.log('Filter parameters:', params);
        
        const response = await axios.get(
            `${config.apiSettings.root}wp-ai-workflows/v1/wp-content`,
            {
                params,
                headers: {
                    'X-WP-Nonce': config.apiSettings.nonce
                }
            }
        );
        
        if (response.data && Array.isArray(response.data)) {
            const formattedPosts = response.data.map(post => ({
                id: post.id,
                title: post.title,
                date: post.date,
                type: post.type,
                link: post.link
            }));
            
            console.log('Formatted posts:', formattedPosts);
            setPosts(formattedPosts);
        } else {
            console.log('Invalid response format:', response.data);
            setPosts([]);
        }
    } catch (error) {
        console.error('Error fetching posts:', error);
        message.error(`Failed to fetch ${contentType} content`);
        setPosts([]);
    } finally {
        setLoadingContent(false);
    }
  };
  
  // Function to add selected posts to vector store
  const addPostsToVectorStore = async () => {
    if (!currentStore || selectedPosts.length === 0) {
      message.error('Please select a vector store and at least one post');
      return;
    }
  
    try {
      setLoadingContent(true);
      
      // Find the selected post type's label for better error messages
      const selectedPostTypeLabel = postTypes.find(pt => pt.name === contentType)?.label || contentType;
      
      const response = await axios.post(
        `${config.apiSettings.root}wp-ai-workflows/v1/vector-stores/${currentStore.id}/add-wp-content`,
        { 
          post_ids: selectedPosts, 
          post_type: contentType 
        },
        { 
          headers: { 
            'X-WP-Nonce': config.apiSettings.nonce,
            'Content-Type': 'application/json'
          } 
        }
      );
      
      if (response.data.success) {
        message.success(`Added ${response.data.added_count} ${selectedPostTypeLabel} items to vector store`);
        
        if (response.data.errors && response.data.errors.length > 0) {
          // Only show errors in console, but give a more user-friendly UI message if some items failed
          console.warn('Some items had errors:', response.data.errors);
          
          if (response.data.added_count < selectedPosts.length) {
            message.warning(`${selectedPosts.length - response.data.added_count} items couldn't be added. Check console for details.`);
          }
        }
        
        fetchStoreFiles(currentStore.id);
        setSelectedPosts([]);
      } else {
        message.error(`Failed to add ${selectedPostTypeLabel} content to vector store`);
      }
    } catch (error) {
      console.error('Error adding posts to vector store:', error);
      message.error(`Failed to add ${contentType} content to vector store. Please check if your server supports this post type.`);
    } finally {
      setLoadingContent(false);
    }
  };
  
  useEffect(() => {
    if (modalVisible && currentStore) {
      fetchCategories();
      fetchAuthors();
    }
  }, [modalVisible, currentStore, fetchCategories, fetchAuthors]);

  // Reset relevant state when content type changes
  useEffect(() => {
    setPosts([]);
    setSelectedPosts([]);
  }, [contentType]);

  const handleCreateStore = () => {
    setCurrentStore(null);
    setFileList([]);
    form.resetFields();
    setModalVisible(true);
  };

  const handleViewFiles = (store) => {
    setCurrentStore(store);
    setFileList([]);
    setModalVisible(true);
    // Fetch files for the selected store
    fetchStoreFiles(store.id);
  };

  const fetchOpenaiVectorStores = async () => {
    try {
      setFetchingOpenaiStores(true);
      setOpenaiStoreError(null);
      
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/openai-vector-stores`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      if (response.data && response.data.data) {
        setOpenaiStores(response.data.data);
      } else {
        setOpenaiStores([]);
      }
    } catch (error) {
      console.error('Error fetching OpenAI vector stores:', error);
      setOpenaiStoreError('Failed to fetch OpenAI vector stores. Please check your API key settings.');
    } finally {
      setFetchingOpenaiStores(false);
    }
  };
  
  const fetchOpenaiStoreFiles = async (storeId) => {
    try {
      setFetchingOpenaiFiles(true);
      
      const response = await axios.get(
        `${config.apiSettings.root}wp-ai-workflows/v1/openai-vector-stores/${storeId}/files`, 
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce
          }
        }
      );
      
      if (response.data && response.data.data) {
        setOpenaiFiles(response.data.data);
      } else {
        setOpenaiFiles([]);
      }
    } catch (error) {
      console.error('Error fetching OpenAI vector store files:', error);
      message.error('Failed to fetch files for this vector store');
      setOpenaiFiles([]);
    } finally {
      setFetchingOpenaiFiles(false);
    }
  };
  
  const handleViewOpenaiStoreFiles = (store) => {
    setSelectedOpenaiStore(store);
    setOpenaiFilesModalVisible(true);
    fetchOpenaiStoreFiles(store.id);
  };

  const fetchStoreFiles = async (storeId) => {
    try {
      setLoading(true);
      
      // Replace with your actual API endpoint for fetching files
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/vector-store-files/${storeId}`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      
      // Transform API response to match Upload component format
      const files = response.data.map(file => ({
        uid: file.id,
        name: file.filename,
        status: 'done',
        url: file.url,
        size: file.size,
        type: file.mime_type,
        uploaded_at: file.created_at
      }));
      
      setFileList(files);
    } catch (error) {
      console.error('Error fetching store files:', error);
      Modal.error({
        title: 'Error Fetching Files',
        content: 'Failed to fetch files for this vector store. Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      if (currentStore) {
        // Update existing store
        await axios.put(
          `${config.apiSettings.root}wp-ai-workflows/v1/vector-stores/${currentStore.id}`,
          values,
          {
            headers: {
              'X-WP-Nonce': config.apiSettings.nonce
            }
          }
        );
      } else {
        // Create new store
        await axios.post(
          `${config.apiSettings.root}wp-ai-workflows/v1/vector-stores`,
          values,
          {
            headers: {
              'X-WP-Nonce': config.apiSettings.nonce
            }
          }
        );
      }
      
      // Refresh vector stores list
      fetchVectorStores();
      setModalVisible(false);
    } catch (error) {
      console.error('Error saving vector store:', error);
      Modal.error({
        title: 'Error',
        content: 'Failed to save vector store. Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStore = async (storeId) => {
    try {
      await axios.delete(
        `${config.apiSettings.root}wp-ai-workflows/v1/vector-stores/${storeId}`,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce
          }
        }
      );
      
      // Refresh vector stores list
      fetchVectorStores();
    } catch (error) {
      console.error('Error deleting vector store:', error);
      Modal.error({
        title: 'Error',
        content: 'Failed to delete vector store. Please try again later.'
      });
    }
  };

  const handleFileUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    
    // Create a FormData instance
    const formData = new FormData();
    formData.append('file', file);
    formData.append('store_id', currentStore.id);
    
    try {
      setUploadingFiles(true);
      
      // Replace with your actual file upload endpoint
      const response = await axios.post(
        `${config.apiSettings.root}wp-ai-workflows/v1/vector-store-files`,
        formData,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      onSuccess(response, file);
      
      // Refresh file list
      fetchStoreFiles(currentStore.id);
    } catch (error) {
      console.error('Error uploading file:', error);
      onError(error);
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDeleteFile = async (file) => {
    try {
      await axios.delete(
        `${config.apiSettings.root}wp-ai-workflows/v1/vector-store-files/${file.uid}`,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce
          }
        }
      );
      
      // Refresh file list
      fetchStoreFiles(currentStore.id);
    } catch (error) {
      console.error('Error deleting file:', error);
      Modal.error({
        title: 'Error',
        content: 'Failed to delete file. Please try again later.'
      });
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Text>{text}</Text>
          {record.is_default && <Tag color="green">Default</Tag>}
        </Space>
      )
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => (
        <Text copyable={{ tooltips: ['Copy ID', 'ID Copied!'] }}>{text}</Text>
      )
    },
    {
      title: 'Files',
      dataIndex: 'file_count',
      key: 'file_count',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            icon={<FileOutlined />} 
            onClick={() => handleViewFiles(record)}
          >
            Files
          </Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => {
              Modal.confirm({
                title: 'Delete Vector Store',
                content: 'Are you sure you want to delete this vector store? This action cannot be undone.',
                onOk: () => handleDeleteStore(record.id)
              });
            }}
          >
            Delete
          </Button>
        </Space>
      )
    }
  ];

  const openaiStoreColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => (
        <Text copyable={{ tooltips: ['Copy ID', 'ID Copied!'] }}>{text}</Text>
      )
    },
    {
      title: 'Files',
      dataIndex: 'file_counts',
      key: 'file_counts',
      render: (counts) => counts ? counts.total : 0
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text * 1000).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button 
          icon={<FileOutlined />}
          onClick={() => handleViewOpenaiStoreFiles(record)}
        >
          View Files
        </Button>
      )
    }
  ];
  
  const openaiFileColumns = [
    {
      title: 'File ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => (
        <Text copyable={{ tooltips: ['Copy ID', 'ID Copied!'] }}>{text}</Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'completed' ? 'green' : status === 'in_progress' ? 'blue' : 'red'}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text * 1000).toLocaleDateString()
    }
  ];

  const renderOpenAITab = () => {
    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <Alert
            message="OpenAI Vector Stores"
            description={
              <div>
                <p>
                  Vector stores allow you to create searchable collections of your documents.
                  When integrated with AI models, vector stores enable context-aware responses based on your content.
                </p>
                <p>
                  To use this feature, you need to have an OpenAI API key configured in the Settings tab.
                  Files uploaded to vector stores will be processed by OpenAI and used to power the file search feature.
                </p>
              </div>
            }
            type="info"
            showIcon
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateStore}
            style={{ marginBottom: 16 }}
          >
            Create Vector Store
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchVectorStores}
            style={{ marginLeft: 8 }}
          >
            Refresh
          </Button>
        </div>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {fetchingStores ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin size="large" />
          </div>
        ) : vectorStores.length === 0 ? (
          <Empty
            description={
              <span>
                No vector stores found. Create your first vector store to get started.
              </span>
            }
          />
        ) : (
          <Table
            dataSource={vectorStores}
            columns={columns}
            rowKey="id"
            loading={fetchingStores}
            pagination={false}
          />
        )}
      </div>
    );
  };

  const renderOpenaiStoresTab = () => {
    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <Alert
            message="OpenAI Vector Stores"
            description={
              <div>
                <p>
                  This list shows all vector stores in your OpenAI account, including those created outside this plugin.
                  You can view file details for each vector store.
                </p>
              </div>
            }
            type="info"
            showIcon
          />
        </div>
        
        <div style={{ marginBottom: 20 }}>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchOpenaiVectorStores}
            loading={fetchingOpenaiStores}
          >
            Refresh Vector Stores
          </Button>
        </div>
        
        {openaiStoreError && (
          <Alert
            message="Error"
            description={openaiStoreError}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        {fetchingOpenaiStores ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin size="large" />
          </div>
        ) : openaiStores.length === 0 ? (
          <Alert
            message="No Vector Stores Found"
            description="No vector stores were found in your OpenAI account."
            type="info"
            showIcon
          />
        ) : (
          <Table
            dataSource={openaiStores}
            columns={openaiStoreColumns}
            rowKey="id"
            pagination={false}
          />
        )}
      </div>
    );
  };

  const renderComingSoonTab = (title, description) => {
    return (
      <div style={{ 
        padding: '40px 20px', 
        textAlign: 'center',
        backgroundColor: '#f9f9f9',
        borderRadius: 8,
      }}>
        <Title level={4}>{title} - Coming Soon</Title>
        <Paragraph>
          {description}
        </Paragraph>
        <div style={{ marginTop: 20 }}>
          <Tag color="blue">Coming Soon</Tag>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <Title level={4}>Knowledge Base Management</Title>
      <Paragraph>
        Create and manage knowledge bases to power AI search capabilities. 
        Upload documents that your AI models can reference when generating responses.
      </Paragraph>
      
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Managed Vector Stores" key="openai">
          {renderOpenAITab()}
        </TabPane>
        <TabPane tab="OpenAI Vector Stores" key="openai-list">
          {renderOpenaiStoresTab()}
        </TabPane>
        <TabPane tab="Pinecone" key="pinecone">
          {renderComingSoonTab("Pinecone Integration", 
            "Integrate with Pinecone to create high-performance vector databases for your AI applications.")}
        </TabPane>
        <TabPane tab="Supabase" key="supabase">
          {renderComingSoonTab("Supabase Integration", 
            "Integrate with Supabase to create Postgres Vector databases for your AI applications.")}
        </TabPane>
        <TabPane tab="Weaviate" key="weaviate">
          {renderComingSoonTab("Weaviate Integration", 
            "Connect with Weaviate to create powerful semantic search capabilities for your content.")}
        </TabPane>
      </Tabs>

      {/* Modal for creating/viewing vector stores */}
      <Modal
        title={currentStore ? `Files in ${currentStore.name}` : "Create Vector Store"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        {currentStore ? (
          <div>
            <Alert
              message="Vector Store Files"
              description={`This vector store can be used with the OpenAI Response API to enhance your AI's knowledge base.`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Tabs defaultActiveKey="files">
              <Tabs.TabPane tab="Upload Files" key="files">
                <Upload
                  customRequest={handleFileUpload}
                  fileList={fileList}
                  multiple={true}
                  onRemove={handleDeleteFile}
                  accept=".pdf,.docx,.doc,.txt,.md,.csv,.json"
                >
                  <Button icon={<CloudUploadOutlined />} loading={uploadingFiles}>
                    Upload Files
                  </Button>
                </Upload>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Supported file types: PDF, Word documents, text files, Markdown, CSV, JSON
                </Text>
              </Tabs.TabPane>
              
              <Tabs.TabPane tab="WordPress Content" key="wordpress">
                <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <Text strong>Content Type</Text>
                      <Select 
                        value={contentType} 
                        onChange={setContentType}
                        style={{ width: '100%', marginTop: 4 }}
                        loading={loadingPostTypes}
                      >
                        {postTypes.map(type => (
                          <Option key={type.name} value={type.name}>{type.label}</Option>
                        ))}
                      </Select>
                    </div>
                    
                    <div style={{ flex: '1 1 200px' }}>
                      <Text strong>Categories</Text>
                      <Select 
                        mode="multiple" 
                        value={categories} 
                        onChange={setCategories}
                        style={{ width: '100%', marginTop: 4 }}
                        options={categoryOptions}
                        loading={loadingCategories}
                        placeholder="All categories"
                      />
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: 8 }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <Text strong>Date Range</Text>
                      <DatePicker.RangePicker 
                        value={dateRange} 
                        onChange={setDateRange} 
                        style={{ width: '100%', marginTop: 4 }}
                      />
                    </div>
                    
                    <div style={{ flex: '1 1 200px' }}>
                      <Text strong>Authors</Text>
                      <Select 
                        mode="multiple" 
                        value={authors} 
                        onChange={setAuthors}
                        style={{ width: '100%', marginTop: 4 }}
                        options={authorOptions}
                        loading={loadingAuthors}
                        placeholder="All authors"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="primary" 
                    onClick={fetchFilteredPosts} 
                    loading={loadingContent}
                    style={{ marginTop: 8 }}
                  >
                    Filter Content
                  </Button>
                </Space>
                
                <div style={{ marginTop: 16 }}>
                  {posts.length > 0 && (
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Checkbox 
                        checked={selectedPosts.length === posts.length}
                        indeterminate={selectedPosts.length > 0 && selectedPosts.length < posts.length}
                        onChange={(e) => {
                          setSelectedPosts(e.target.checked ? posts.map(post => post.id) : []);
                        }}
                      >
                        Select All ({posts.length} items)
                      </Checkbox>
                      
                      <Button 
                        type="primary" 
                        disabled={selectedPosts.length === 0}
                        onClick={addPostsToVectorStore}
                        loading={loadingContent}
                      >
                        Add {selectedPosts.length} Selected
                      </Button>
                    </div>
                  )}
                  
                  <Table 
                    dataSource={posts}
                    rowKey="id"
                    loading={loadingContent}
                    rowSelection={{
                      selectedRowKeys: selectedPosts,
                      onChange: setSelectedPosts,
                      getCheckboxProps: (record) => ({
                        disabled: loadingContent,
                      }),
                    }}
                    columns={[
                      {
                        title: 'Title',
                        dataIndex: 'title',
                        render: (text, record) => (
                          <a href={record.link} target="_blank" rel="noopener noreferrer">
                            {text}
                          </a>
                        )
                      },
                      {
                        title: 'Date',
                        dataIndex: 'date',
                        width: 120,
                        render: text => new Date(text).toLocaleDateString()
                      },
                      {
                        title: 'Type',
                        dataIndex: 'type',
                        width: 80,
                        render: type => {
                          // Find the post type label from the postTypes array
                          const postType = postTypes.find(pt => pt.name === type);
                          return postType ? postType.label : type.charAt(0).toUpperCase() + type.slice(1);
                        }
                      }
                    ]}
                    pagination={{ pageSize: 10 }}
                    size="small"
                    locale={{ emptyText: 'Use filters above to find content' }}
                  />
                </div>
              </Tabs.TabPane>
            </Tabs>
            
            {loading && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Spin />
              </div>
            )}
          </div>
        ) : (
          // Form for creating new vector store remains the same
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="name"
              label="Vector Store Name"
              rules={[{ required: true, message: 'Please enter a name for the vector store' }]}
            >
              <Input placeholder="My Knowledge Base" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create Vector Store
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title={selectedOpenaiStore ? `Files in ${selectedOpenaiStore.name}` : "Vector Store Files"}
        open={openaiFilesModalVisible}
        onCancel={() => setOpenaiFilesModalVisible(false)}
        footer={null}
        width={800}
      >
        <div>
          <Alert
            message="OpenAI Vector Store Files"
            description={`These files are stored in your OpenAI account and can be used with the OpenAI API.`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          {fetchingOpenaiFiles ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Spin size="large" />
            </div>
          ) : openaiFiles.length === 0 ? (
            <Alert
              message="No Files Found"
              description="No files were found in this vector store."
              type="info"
              showIcon
            />
          ) : (
            <Table
              dataSource={openaiFiles}
              columns={openaiFileColumns}
              rowKey="id"
              pagination={false}
            />
          )}
        </div>
      </Modal>
    </Card>
  );
};

export default VectorStoreManagement;